import AnimatedSprite, {
  AnimatedSpritePlay,
} from "components/Media/AnimatedSprite";
import { Redirect, useParams } from "react-router-dom";
import { ReplicaType, ReplicaVersionType } from "types";
import replicas, { replicasWIP } from "assets/replicas";

import Carousel from "components/Carousel";
import LayoutPage from "layouts/LayoutPage";
import { Link } from "react-router-dom";
import ReplicaBigTitle from "components/Replicas/ReplicaBigTitle";
import ReplicaItem from "components/Replicas/ReplicaItem";
import ReplicaList from "pages/Replicas/ReplicaList";
import ReplicaParagraph from "components/Replicas/ReplicaParagraph";
import ReplicaSpacer from "components/Replicas/ReplicaSpacer";
import ReplicaSubTitleAnchor from "components/Replicas/ReplicaSubTitleAnchor";
import SectionHeading from "components/SectionHeading";
import StructuredHeading from "components/Replicas/StructuredHeading";
import { routes } from "config";
import styled from "styled-components";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
`;

const VersionPages = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const VersionWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const VersionContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 4vh;

  ${(props) => props.theme.breakpoints.M} {
    margin-top: 30px;
  }
`;
const VersionTitle = styled.h2`
  margin-top: -8px;
  span {
    opacity: 0.5;
    font-size: 0.6em;
  }
  strong {
    font-weight: 500;
  }
`;

const Thumbnail = styled.div`
  display: none;
  ${(props) => props.theme.breakpoints.M} {
    display: block;
  }

  img {
    height: 300px;

    object-fit: contain;

    --blur: 1rem;
    --offsetY: 1rem;
    --saturate: 0.8;
    filter: drop-shadow(
        0 var(--offsetY) var(--blur) ${(props) => props.theme.colors.black}88
      )
      saturate(var(--saturate));
  }
`;

const Meta = styled.div`
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBubble = styled.div`
  padding: 3px 10px;
  border-radius: 10px;

  background: #000;
  color: ${(props) => props.theme.colors.white}88;
  border: 1px solid ${(props) => props.theme.colors.white}33;

  font-size: 10px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.05em;
`;

const ReplicaDetail = () => {
  const { id, version } = useParams();

  let replica: ReplicaType = replicas.find((r) => r.id === id);
  let isWIP = false;

  if (!replica) {
    // If the replica doesn't exist, try to find it in WIP replicas
    replica = replicasWIP.find((r) => r.id === id);
    isWIP = true;
  }

  if (!replica) {
    // If the replica still doesn't exist, try to find alternative id and redirect to current id
    // (for backwards compatibility with old URLs)
    const replicaByAlternativeId = replicas.find((r) =>
      r.alternativeIds?.includes(id),
    );
    if (replicaByAlternativeId) {
      return (
        <Redirect
          to={"/" + routes.replicas.slug + "/" + replicaByAlternativeId.id}
        />
      );
    }
  }

  if (!replica) {
    // If the replica still doesn't exist, redirect to replicas main page
    return <Redirect to={"/" + routes.replicas.slug} />;
  }

  // Find the correct version of the replica to display (based on URL param)
  const latestVersion: ReplicaVersionType = replica.versions[0];
  const replicaVersion: ReplicaVersionType = !version
    ? latestVersion
    : replica.versions.find((v) => `v${v.version}` === version);

  if (!replicaVersion) {
    // If the version doesn't exist, redirect to replicas main page
    return <Redirect to={"/" + routes.replicas.slug + "/" + replica.id} />;
  }

  const hasMultipleVersions = replica?.versions?.length > 1;
  const isLatestVersion = replicaVersion.version === latestVersion.version;

  const otherVersions: ReplicaVersionType[] = hasMultipleVersions
    ? [
        ...replica?.versions
          .filter((r) => r.version !== replicaVersion.version)
          .reverse(),
      ]
    : [];

  const similarReplicas: ReplicaType[] = replicas.filter(
    (r) => r.category === replica.category && r.id !== replica.id,
  );

  //
  // RENDER FUNCTIONS
  //
  const renderVersionTitle = (version: ReplicaVersionType) => {
    return (
      <VersionTitle>
        <span>
          <strong>{`v${version.version}`}</strong>
          {`${version.isSeries ? " (Series)" : ""}`}
        </span>
      </VersionTitle>
    );
  };

  const renderPage = (version: ReplicaVersionType) => {
    return (
      <>
        <Meta>
          {version?.dateCreated && (
            <MetaBubble>
              {`finished ${version.dateCreated.toLocaleDateString("en-US", {
                year: "numeric", // "2025"
                month: "long", // "June"
              })}`}
            </MetaBubble>
          )}
          {version?.datePublished && (
            <MetaBubble>
              {`published ${version.datePublished.toLocaleDateString("en-US", {
                year: "numeric", // "2025"
                month: "long", // "June"
              })}`}
            </MetaBubble>
          )}
          <MetaBubble>by Jan Hamernik</MetaBubble>
        </Meta>
        <VersionContent>{version.page}</VersionContent>{" "}
      </>
    );
  };

  //
  // RENDER
  //
  return (
    <LayoutPage
      background={replicaVersion.background}
      backgroundVideo={replicaVersion.backgroundVideo}
      backgroundVideoFade={0.15}
    >
      <Wrapper>
        {isWIP ? (
          <ReplicaSubTitleAnchor title="Work In Progress" level={2} />
        ) : null}

        {!isLatestVersion ? (
          <Link
            to={`/${routes.replicas.slug}/${replica.id}`}
            className="link-as-button"
          >
            {`See the latest version (v${latestVersion?.version})`}
          </Link>
        ) : null}

        <SectionHeading
          title={
            <StructuredHeading
              nameFormatted={replica.nameFormatted}
              name={replica.name}
              versionTitle={
                hasMultipleVersions && renderVersionTitle(replicaVersion)
              }
            />
          }
          subtitle={replica.origin}
          link={`/${routes.replicas.slug}`}
          linkTitle={"Prop Replicas"}
        />

        <Thumbnail>
          <AnimatedSprite
            src={replica.versions[0].thumbnail}
            alt={replica.name}
            animation={{
              play: AnimatedSpritePlay.infinite,
              frameDuration_Milliseconds: 50,
            }}
          />
        </Thumbnail>

        <VersionPages>
          <VersionWrapper>{renderPage(replicaVersion)}</VersionWrapper>

          {/*
           **   OTHER VERSIONS
           */}
          {otherVersions?.length ? (
            <>
              <ReplicaBigTitle
                title={isLatestVersion ? "Previous Versions" : "Other Versions"}
              />
              <ReplicaParagraph>
                For archiving purposes, below you will find all the other
                versions of this replica. My goal is to keep the description of
                the selected version (above) concise and independent of the
                others, so some of the information below may be repeated. If
                you’re interested in the history of this replica, or in seeing
                what changed between versions, feel free to check out the
                sections below.
              </ReplicaParagraph>

              <ReplicaList
                renderItems={() =>
                  [...otherVersions].map((replicaVersion) => (
                    <ReplicaItem
                      key={replicaVersion.version}
                      replica={replica}
                      version={replicaVersion.version}
                    />
                  ))
                }
              />
            </>
          ) : null}

          {/*
           **   SIMILAR REPLICAS
           */}
          {similarReplicas?.length ? (
            <>
              <ReplicaSpacer />
              <ReplicaSpacer />
              <ReplicaSpacer />
              <ReplicaSpacer />
              <ReplicaBigTitle title="You may also like" />

              {/* padding is necessary for replicas with large thumbnails (absolute positioning results in these being clipped) */}
              <Carousel
                items={[...similarReplicas].map((replica) => (
                  <ReplicaItem replica={replica} />
                ))}
                autoplay
                padding="30px 0 30px 0"
              />
            </>
          ) : null}
        </VersionPages>
      </Wrapper>
    </LayoutPage>
  );
};

export default ReplicaDetail;
